import React, { useEffect, useRef } from 'react';
import type { ListChildComponentProps } from 'react-window';
import { areEqual } from 'react-window';
import { styled } from 'baseui';
import { useFocusedAnnotationId } from '../hooks/use-focused-annotation';
import { useSelectedAnnotationIds } from '../hooks/use-selected-annotation-ids';
import type { VirtualListDataT } from './types';
import { getAnnotationItemKey } from './utils';
const AnnotationItemContainer = styled<'div', {
  $disabled: boolean;
}>('div', ({
  $disabled,
  $theme
}) => ({
  cursor: $disabled ? 'not-allowed' : 'pointer',
  marginBottom: $theme.sizing.scale300
}));
function VirtualListItemRendererMemoized({
  index,
  style,
  data
}: ListChildComponentProps<VirtualListDataT>) {
  const rowRef = useRef<HTMLDivElement>(null);
  const {
    items,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onKeyDown,
    headerComponent,
    itemComponent,
    setSize
  } = data;
  const selectedAnnotationIds = useSelectedAnnotationIds();
  const focusedAnnotationId = useFocusedAnnotationId();
  const listItem = items[index];
  const itemKey = listItem?.type === 'annotation' ? getAnnotationItemKey(listItem.annotation.id) : null;
  useEffect(() => {
    if (rowRef.current && itemKey) {
      setSize(itemKey, rowRef.current.getBoundingClientRect().height);
    }
  }, [setSize, itemKey, listItem]);
  if (!listItem) {
    return null;
  }
  if (listItem.type === 'header') {
    return <div style={{
      position: style.position,
      left: style.left,
      top: style.top,
      width: style.width
    }}>
        {headerComponent(listItem.headerInfo, listItem.label, listItem.icon)}
      </div>;
  }
  const {
    annotation,
    icon
  } = listItem;
  const annotationId = annotation.id;
  const isSelected = selectedAnnotationIds.includes(annotationId);
  const isFocused = focusedAnnotationId === annotationId;
  const isDisabled = annotation.isDisabled;
  const interactionProps = isDisabled ? {} : {
    role: 'button',
    tabIndex: 0,
    onMouseEnter: () => onMouseEnter(annotation.id),
    onMouseLeave: () => onMouseLeave(null),
    onClick: () => onClick(annotation.id),
    onKeyDown: (e: React.KeyboardEvent) => onKeyDown(e, annotation.id)
  };
  return <div ref={rowRef} style={{
    position: style.position,
    left: style.left,
    top: style.top,
    width: style.width
  }}>
      <AnnotationItemContainer data-testid={`annotation-${annotation.id}`} $disabled={isDisabled} {...interactionProps}>
        {itemComponent({
        ...annotation,
        isSelected
      }, isFocused, icon)}
      </AnnotationItemContainer>
    </div>;
}
export const VirtualListItemRenderer = React.memo(VirtualListItemRendererMemoized, areEqual);